import React, { useState, useEffect, useContext } from "react";
import { TextInput, GooglePlaceSearchComponent, Button, Checkbox, ConfirmationDialog, TextArea, FormErrorMessage } from "../../../components";
import { formatPhoneNumber, formatPostalCode, numberOnly, validateEmail, onFileError } from "../../../utils";
import { ToastContext } from "../../../contexts/ToastContext/ToastContext";
import { useSelector, useDispatch } from "react-redux";
import { RootState, AppDispatch } from "../../../redux/store";
import { AuthPermission } from "../../../services/auth.service";
import { UpdateOffice } from "../../../models/update-office.model";
import { deleteOfficeFile, getOffice, getOfficeHeadLogo, updateOffice } from "../../../redux/slices/OfficeSlice";
import { FormFieldType, formDataInitialState, formDataType, hoursList, officeWeekDayTimings } from "./OfficeProfileData";
import { Phone_Fax_Regex, colors } from "../../../utils/constants";
import RadioInput from "../../../components/RadioInput/RadioInput";
import moment from "moment";
import SelectNew from "../../../components/Select/SelectNew";
import { OfficeService } from "../../../services/office.service";
import { OfficeState } from "../../../models/office.model";
import AddressField, { AddressFieldValue } from "../../../components/CustomAddress/CustomAddress";
import { Modal } from "@mui/material";
import close from "../../../assets/images/close.svg";
import { UserType } from "../../../services/user.service";
import { getPssData } from "../../../redux/slices/CurrentUserSlice";
import DropArea from "../../../components/DropArea/DropArea";
import { uploadFile, getFile } from "../../../redux/slices/FileSlice";
import { useParams } from "react-router-dom";
import { FileTypes, ToastVariants } from "../../../models";
import Delete from "../../../assets/images/delete-file.svg";
import { DeleteOutline } from "@mui/icons-material";

const STREET_KEY = "route";
const CITY_KEY = ["locality", "sublocality_level_1"];
const STATE_KEY = "administrative_area_level_1";
const ZIP_KEY = ["zip_code", "postal_code"];
const COUNTRY_KEY = "country";
const STREET_NUMBER_KEY = "street_number";
const UNIT = "subpremise";

interface GoogleAddressCompType {
  long_name: string;
  short_name: string;
  types: string[];
}

interface GoogleAPIResponseType {
  address_components: GoogleAddressCompType[];
}

const getKeyValueOfAddress = (data: GoogleAPIResponseType | null, key: string | string[], longValue?: boolean) => {
  if (data === null) return "";

  if (Array.isArray(key)) {
    for (let i = 0; i < key.length; i++) {
      const k = key[i];
      const addressComp: GoogleAddressCompType[] = data["address_components"];
      const keyArr = addressComp?.find(addrCom => addrCom.types.includes(k));
      if (keyArr !== undefined) {
        return keyArr[longValue ? "long_name" : "short_name"];
      }
    }
  } else {
    const addressComp: GoogleAddressCompType[] = data["address_components"];
    const keyArr = addressComp?.find(addrCom => addrCom.types.includes(key));
    return keyArr ? keyArr[longValue ? "long_name" : "short_name"] : "";
  }

  return "";
};

const validateAddress = (value: GoogleAddressCompType[]) => {
  const city = getKeyValueOfAddress({ address_components: value }, CITY_KEY);
  let streetNumber = getKeyValueOfAddress({ address_components: value }, STREET_NUMBER_KEY);
  if (typeof streetNumber !== "string") {
    streetNumber = "";
  }
  const route = getKeyValueOfAddress({ address_components: value }, STREET_KEY, true);
  const unit = getKeyValueOfAddress({ address_components: value }, UNIT);
  const state = getKeyValueOfAddress({ address_components: value }, STATE_KEY);
  const zip = getKeyValueOfAddress({ address_components: value }, ZIP_KEY[0])
    ? getKeyValueOfAddress({ address_components: value }, ZIP_KEY[0])
    : getKeyValueOfAddress({ address_components: value }, ZIP_KEY[1]);
  const country = getKeyValueOfAddress({ address_components: value }, COUNTRY_KEY);

  // Combine street_number + route into addressLine1 (saved as 'street' in DB)
  // Use subpremise (unit) as addressLine2 (saved as 'street_1' in DB) - prepend "Suite " if subpremise is a simple unit number
  const addressLine1 = [streetNumber, route].filter(Boolean).join(" ").trim();
  // Only prepend "Suite " if the unit is a simple value (no spaces, doesn't already start with common prefixes)
  const shouldPrependSuite = unit && !unit.includes(" ") && !/^(suite|floor|unit|apt|apartment|ste|fl)/i.test(unit);
  const addressLine2 = unit ? (shouldPrependSuite ? `Suite ${unit}` : unit) : "";

  const formattedZip = formatPostalCode(zip);
  const addressObj = { city, state, addressLine1, addressLine2, zip: formattedZip, country };
  if (!city || !state || !formattedZip || !country || !addressLine1) {
    return null;
  }

  return addressObj;
};

const OfficeProfileTab = () => {
  const dispatch: AppDispatch = useDispatch();
  const { id } = useParams();
  const [errorInitiated, setErrorInitiated] = useState(false);
  const [formData, setFormData] = useState([...formDataInitialState]);
  const [weekDayTimings, setWeekDayTimings] = useState([...officeWeekDayTimings]);
  const toast = useContext(ToastContext);
  const { currentOffice, loading } = useSelector((state: RootState) => state.office);
  const [readOnly] = useState(true);
  const [canEdit, setCanEdit] = useState(false);
  const [isLoading, setLoading] = useState(false);

  // Letterhead component state
  const [letterheadContent, setLetterheadContent] = useState("");
  const [showLetterheadPreview, setShowLetterheadPreview] = useState(false);
  const [letterheadLogoFile, setLetterheadLogoFile] = useState<File | null>(null);
  const [letterheadLogoFileData, setLetterheadLogoFileData] = useState<any>(null);
  const [letterheadLogoDropAreaError, setLetterheadLogoDropAreaError] = useState<string>("");
  const [letterHeaderLogoFileId, setLetterHeaderLogoFileId] = useState<string | undefined>(currentOffice?.letterHeaderLogo);
  const [letterheadLogoPreviewUrl, setLetterheadLogoPreviewUrl] = useState<string | null>(null);

  // Map office address fields to Google address component format
  // street = full street address (e.g., "123 Queen St W") -> street_number
  // street1 = suite/unit (e.g., "Suite 500") -> subpremise
  const initialAddressData = [
    { short_name: `${currentOffice?.street ?? ""}`, long_name: `${currentOffice?.street ?? ""}`, types: ["street_number"] },
    {
      short_name: `${currentOffice?.city ?? ""}`,
      long_name: `${currentOffice?.city ?? ""}`,
      types: ["sublocality_level_1", "sublocality", "political"],
    },
    { short_name: `${currentOffice?.city ?? ""}`, long_name: `${currentOffice?.city ?? ""}`, types: ["locality", "political"] },
    { short_name: `${currentOffice?.state ?? ""}`, long_name: `${currentOffice?.state ?? ""}`, types: ["administrative_area_level_2", "political"] },
    { short_name: `${currentOffice?.state ?? ""}`, long_name: `${currentOffice?.state ?? ""}`, types: ["administrative_area_level_1", "political"] },
    { short_name: `${currentOffice?.country ?? ""}`, long_name: `${currentOffice?.country ?? ""}`, types: ["country", "political"] },
    { short_name: `${currentOffice?.zip ?? ""}`, long_name: `${currentOffice?.zip ?? ""}`, types: ["postal_code"] },
    // Only include subpremise if street1 has a value
    ...(currentOffice?.street1 ? [{ short_name: currentOffice.street1, long_name: currentOffice.street1, types: ["subpremise"] }] : []),
  ];
  const [officeAddress, setOfficeAddress] = useState<AddressFieldValue>({
    value: "",
    isValid: false,
    components: initialAddressData,
  });

  const [addressValidate, setAddressValidate] = useState(false);
  const [showTemporarilyCloseDialog, setShowTemporarilyCloseDialog] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [reasonText, setReasonText] = useState("");
  const [useCustomAddress, setUseCustomAddress] = useState(false);

  const { currentUser } = useSelector((state: RootState) => state.currentUser);
  const permissions = currentUser?.permissions;
  const isSystemAdmin = currentUser?.userType === UserType.SystemAdmin;

  const isAuthorizedToEdit = Array.isArray(permissions) && permissions.includes(AuthPermission.Office);

  const validTimings = weekDayTimings.every(item => item.valid);

  // Letterhead handlers
  const handleLetterheadChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    // if (!isSystemAdmin) {
    setLetterheadContent(e.target.value);
    // }
  };

  const handleLetterheadPreview = () => {
    if (letterheadContent?.trim()) {
      setShowLetterheadPreview(true);
    }
  };

  const closeLetterheadPreview = () => {
    setShowLetterheadPreview(false);
  };

  const handleChange = (id: string, val: string | boolean) => {
    let tempFormData = [...formData];
    const index = tempFormData.findIndex(item => item.id === id);
    const item = tempFormData.find(item => item.id === id);
    if (index !== -1 && item) {
      let value = val;
      let valid = item.valid;
      switch (id) {
        case "officePhone":
        case "officeFax":
          value = formatPhoneNumber(value as string);
          valid = true;
          break;
        case "additionalPhone": {
          const digitsOnly = (value as string).replace(/\D/g, "").slice(0, 6);
          value = digitsOnly;
          const len = digitsOnly.length;
          valid = len === 0 || (len >= 3 && len <= 6);
          break;
        }
        case "primaryContactEmail":
          valid = validateEmail(value as string);
          break;
        case "familyPhysician":
          if (value === "Yes") {
            tempFormData = tempFormData.map(item => {
              let newItem = { ...item };
              if (item.id === "specialty") {
                newItem = { ...newItem, visible: false };
              }
              if (["inPhysicianGroups", "patientRosterSize", "acceptingPatients"].includes(item.id)) {
                newItem = { ...newItem, visible: true };
              }
              return newItem;
            });
          } else if (value === "No") {
            tempFormData = tempFormData.map(item => {
              let newItem = { ...item };
              if (item.id === "specialty") {
                newItem = { ...newItem, visible: true };
              }
              if (["inPhysicianGroups", "physicianGroups", "patientRosterSize", "acceptingPatients"].includes(item.id)) {
                newItem = { ...newItem, visible: false };
              }
              return newItem;
            });
          }
          valid = true;
          break;
        case "inPhysicianGroups":
          if (value === "Yes") {
            tempFormData = tempFormData.map(item => {
              let newItem = { ...item };
              if (item.id === "physicianGroups") {
                newItem = { ...newItem, visible: true };
              }
              return newItem;
            });
          } else {
            tempFormData = tempFormData.map(item => {
              let newItem = { ...item };
              if (item.id === "physicianGroups") {
                newItem = { ...newItem, visible: false };
              }
              return newItem;
            });
          }
          valid = true;
          break;
        case "acceptingPatients":
          valid = true;
          break;
        case "yearsInPractice":
          value = numberOnly(value as string);
          // valid = value?.trim().length > 0;
          valid = true; // Changed from: valid = value?.trim().length > 0;
          break;
        default:
          valid = item.required ? (typeof value === "string" ? value.length > 0 : value === true) : true;
          break;
      }
      tempFormData = [...tempFormData.slice(0, index), { ...item, value, valid }, ...tempFormData.slice(index + 1)];
      setFormData([...tempFormData]);
    }
  };

  const handleChangeTime = (item: any, index: number, key: string, val: string | boolean) => {
    let tempFormData = [...weekDayTimings];
    const inputTime = moment(val as string, "HH:mm:ss");
    if (index !== -1 && item) {
      if (
        (key === "open" && item.close && !inputTime.isBefore(moment(item.close, "HH:mm:ss"))) ||
        (key === "close" && item.open && !inputTime.isAfter(moment(item.open, "HH:mm:ss")))
      ) {
        toast?.openToast(key === "open" ? "Open time can't be after or same as close time." : "Close time can't be before or same as open time.");
        return;
      }
      const newItem = { ...item, [key]: val };
      switch (key) {
        case "open":
        case "close":
          tempFormData = [
            ...tempFormData.slice(0, index),
            {
              ...newItem,
              valid: newItem.open && newItem.close,
            },
            ...tempFormData.slice(index + 1),
          ];
          break;
        case "closed":
          tempFormData = [
            ...tempFormData.slice(0, index),
            {
              ...newItem,
              valid: newItem.closed,
              open: "",
              close: "",
            },
            ...tempFormData.slice(index + 1),
          ];
          break;
        default:
          break;
      }
      setWeekDayTimings([...tempFormData]);
    }
  };

  const validateForm = () => {
    // return formData.every(val => val?.valid) && (!currentOffice?.tempClosed ? validTimings : true);
    return formData.every(val => val?.valid);
  };

  const getAddOfficeData = () => {
    return formDataInitialState
      .map(item => item.id)
      .map(key => {
        const foundVal = formData?.find(el => el.id === key)?.value as string;
        return {
          [key]: foundVal ? foundVal : "",
        };
      })
      .reduce((acc, el) => {
        return { ...acc, ...el };
      }, {});
  };

  const getTimings = () => {
    const officeTimingsObject = weekDayTimings.reduce((acc: any, day: any) => {
      if (day.closed) {
        acc[day.key3] = day.closed;
      }
      if (day.open) {
        acc[day.key1] = moment(day.open, "hh:mm A").format("HH:mm:ss");
      }
      if (day.close) {
        acc[day.key2] = moment(day.close, "hh:mm A").format("HH:mm:ss");
      }
      return acc;
    }, {});

    return officeTimingsObject;
  };

  const saveOfficeProfile = async () => {
    if (currentOffice?.officeId) {
      const {
        officeName,
        officePhone,
        officeFax,
        additionalPhone,
        primaryContactEmail,
        primaryContact,
        inPhysicianGroups,
        familyPhysician,
        acceptingPatients,
      } = getAddOfficeData();
      const timings = getTimings();
      const addressValidation = validateAddress(officeAddress.components);
      if (addressValidation === null) {
        setAddressValidate(true);
        return;
      }

      const dataToSend: UpdateOffice = {
        ...getAddOfficeData(),
        officeId: currentOffice?.officeId,
        name: officeName,
        city: addressValidation?.city,
        street: addressValidation.addressLine1,
        street1: addressValidation.addressLine2,
        street2: "",
        state: addressValidation?.state,
        zip: addressValidation?.zip,
        country: addressValidation?.country,
        phone: officePhone,
        fax: officeFax,
        additionalPhone: additionalPhone,
        customOfficeAddress: useCustomAddress,
        contactName: primaryContact,
        contactEmail: primaryContactEmail,
        inPhysicianGroups: inPhysicianGroups === "Yes" ? true : inPhysicianGroups === "No" ? false : undefined,
        familyPhysician: familyPhysician === "Yes" ? true : familyPhysician === "No" ? false : undefined,
        acceptingPatients: acceptingPatients === "Yes" ? true : acceptingPatients === "No" ? false : undefined,
        letterHeader: letterheadContent,
        isLetterHeaderAddress: true,
        ...timings,
      };
      if (letterHeaderLogoFileId) {
        dataToSend.letterHeaderLogo = letterHeaderLogoFileId;
      } else {
        // Keep existing letter logo if no new file is uploaded
        dataToSend.letterHeaderLogo = currentOffice.letterHeaderLogo;
      }

      // Upload letter logo file if it's a new File
      // if (letterheadLogoFile instanceof File) {
      //   // Upload the file directly without conversion (unlike signature which gets converted to PDF)
      //   const uploadResult = await dispatch(uploadFile({ file: letterheadLogoFile, callback: () => {} }));
      //   if (uploadResult.payload) {
      //     dataToSend.letterHeaderLogo = (uploadResult.payload as any).fileId;
      //   }
      // } else if (currentOffice?.letterHeaderLogo) {
      //   // Keep existing letter logo if no new file is uploaded
      //   dataToSend.letterHeaderLogo = currentOffice.letterHeaderLogo;
      // }
      setErrorInitiated(false);
      dispatch(updateOffice(dataToSend)).then(async act => {
        const payload: any = act.payload;
        const hasErrorMessage = payload && typeof payload === "object" && "message" in payload;
        if (act.payload !== null && !hasErrorMessage && currentOffice.officeId) {
          await dispatch(getOffice({ officeId: currentOffice.officeId }));
          toast?.openToast("Updated Successfully!");
          setCanEdit(false);
          setLetterheadLogoFile(null);
        } else {
          const errorMessage = (payload as any)?.message || "Failed to save. Please try again.";
          toast?.openToast(errorMessage, 3000, ToastVariants.Error);
        }
      });
    }
  };

  useEffect(() => {
    if (!currentOffice) return;

    setUseCustomAddress(!!currentOffice.customOfficeAddress);

    setFormData(populateFormData(currentOffice));
    setWeekDayTimings(populateTimingData(currentOffice));

    // Load letterhead content
    if (currentOffice?.letterHeader) {
      setLetterheadContent(currentOffice.letterHeader?.letterHeader);
    }

    // Load letterhead logo file data if it exists
    if (currentOffice?.letterHeaderLogo) {
      dispatch(getOfficeHeadLogo({ officeId: currentOffice.officeId }))
        .then(action => {
          if (action.payload) {
            setLetterheadLogoFileData(action.payload);
          }
        })
        .catch(error => {});
    } else {
      setLetterheadLogoFileData(null);
    }
  }, [currentOffice, dispatch]);

  useEffect(() => {
    const fetchID = currentOffice?.officeId ? currentOffice?.officeId : id;
    if (fetchID) dispatch(getOffice({ officeId: fetchID }));
  }, []);

  // Clear unsaved file when exiting edit mode
  useEffect(() => {
    if (!canEdit) {
      setLetterheadLogoFile(null);
      setLetterheadLogoDropAreaError("");
      // Cleanup preview URL when exiting edit mode
      if (letterheadLogoPreviewUrl) {
        URL.revokeObjectURL(letterheadLogoPreviewUrl);
        setLetterheadLogoPreviewUrl(null);
      }
    }
  }, [canEdit, letterheadLogoPreviewUrl]);

  // Create preview URL for newly uploaded file
  useEffect(() => {
    if (letterheadLogoFile) {
      const objectUrl = URL.createObjectURL(letterheadLogoFile);
      setLetterheadLogoPreviewUrl(objectUrl);

      // Cleanup function
      return () => {
        URL.revokeObjectURL(objectUrl);
      };
    } else {
      setLetterheadLogoPreviewUrl(null);
    }
  }, [letterheadLogoFile]);

  // Cleanup preview URL on component unmount
  useEffect(() => {
    return () => {
      if (letterheadLogoPreviewUrl) {
        URL.revokeObjectURL(letterheadLogoPreviewUrl);
      }
    };
  }, [letterheadLogoPreviewUrl]);

  useEffect(() => {
    if (!currentOffice) return;

    // Map DB fields to component types for CustomAddress:
    // street (address line 1) → street_number
    // street1 (address line 2) → subpremise
    const comps: GoogleAddressCompType[] = [
      { short_name: currentOffice?.street, long_name: currentOffice.street, types: ["street_number"] },
      { short_name: currentOffice.city, long_name: currentOffice.city, types: ["locality", "political"] },
      { short_name: currentOffice.state, long_name: currentOffice.state, types: ["administrative_area_level_1", "political"] },
      { short_name: currentOffice.zip, long_name: currentOffice.zip, types: ["postal_code"] },
      { short_name: currentOffice.country, long_name: currentOffice.country, types: ["country", "political"] },
      ...(currentOffice?.street1 ? [{ short_name: currentOffice.street1, long_name: currentOffice.street1, types: ["subpremise"] }] : []),
    ];

    const formatted = [
      currentOffice?.street,
      currentOffice?.street1,
      // currentOffice?.street2,
      currentOffice?.city,
      currentOffice?.state,
      currentOffice?.zip,
      currentOffice?.country,
    ]
      .filter(Boolean)
      .join(", ");

    setOfficeAddress({
      value: formatted,
      isValid: true,
      components: comps,
    });
  }, [currentOffice]);

  useEffect(() => {
    setFormData(prev => {
      const idx = prev.findIndex(f => f.id === "officeAddress");
      if (idx === -1) return prev;

      const next = [...prev];
      next[idx] = {
        ...next[idx],
        value: officeAddress?.value,
        valid: officeAddress?.isValid,
      };
      return next;
    });
  }, [officeAddress?.value, officeAddress?.isValid]);

  const renderInput = (inp: formDataType, isReadOnly: boolean, index: number) => {
    if (typeof inp?.visible === "undefined" || inp?.visible) {
      switch (inp?.type) {
        case FormFieldType.SectionHeader:
          return (
            <>
              <div className="flex border-b border-docsigna-purple border-opacity-40 py-1 my-2 col-span-2">
                <h2 className="uppercase text-lg font-bold">{inp.title}</h2>
                {inp.required && canEdit ? <span className="text-red-500">*</span> : null}
              </div>
            </>
          );
        case FormFieldType.TextInput:
          if (inp.id === "officePhone") {
            return null;
          }

          if (inp.id === "additionalPhone") {
            const officePhoneItem = formData.find(f => f.id === "officePhone");
            if (!officePhoneItem) return null;

            return (
              <div className="col-span-2 flex flex-col md:flex-row gap-5 w-full">
                <div className="w-full md:w-[40%]">
                  {isReadOnly ? (
                    <div className="w-full">
                      <label className="text-sm text-docsigna-blue-dark font-medium block w-full max-w-full mb-1">{inp?.title}</label>
                      <h2 className="text-lg font-medium text-docsigna-purple">{inp?.value}</h2>
                    </div>
                  ) : (
                    <TextInput
                      key={inp.id}
                      value={inp.value as string}
                      onChangeFunc={e => handleChange(inp.id, e.currentTarget.value)}
                      placeholder={inp.placeholder}
                      errorMsg={inp.errorMessage}
                      isError={!inp.valid && errorInitiated}
                      label={inp.title}
                      isRequired={inp.required}
                      disabled={isReadOnly}
                      onBlur={e => handleChange(inp.id, e.currentTarget.value.trim())}
                    />
                  )}
                </div>
                <div className="w-full md:w-[60%]">
                  {isReadOnly ? (
                    <div className="w-full">
                      <label className="text-sm text-docsigna-blue-dark font-medium block w-full max-w-full mb-1">{officePhoneItem?.title}</label>
                      <h2 className="text-lg font-medium text-docsigna-purple">{officePhoneItem?.value}</h2>
                    </div>
                  ) : (
                    <TextInput
                      key={officePhoneItem.id}
                      value={officePhoneItem.value as string}
                      onChangeFunc={e => handleChange(officePhoneItem.id, e.currentTarget.value)}
                      placeholder={officePhoneItem.placeholder}
                      errorMsg={officePhoneItem.errorMessage}
                      isError={!officePhoneItem.valid && errorInitiated}
                      label={officePhoneItem.title}
                      isRequired={officePhoneItem.required}
                      disabled={isReadOnly}
                      onBlur={e => handleChange(officePhoneItem.id, e.currentTarget.value.trim())}
                    />
                  )}
                </div>
              </div>
            );
          }

          const alwaysFullWidthInputs = ["officeName"];
          const fullWidthInputs = ["officeAddress", ...alwaysFullWidthInputs];

          const isFullWidth = fullWidthInputs.includes(inp.id);
          const isAlwaysFullWidth = alwaysFullWidthInputs.includes(inp.id);

          const colClass = isAlwaysFullWidth ? "col-span-2" : isFullWidth && !isReadOnly ? "col-span-2" : "col-span-2 md:col-span-1";

          return (
            <>
              <div className={colClass}>
                {isReadOnly ? (
                  <div className="w-full">
                    <label className="text-sm text-docsigna-blue-dark font-medium block w-full max-w-full mb-1">{inp?.title}</label>
                    <h2 className="text-lg font-medium text-docsigna-purple">{inp?.value}</h2>
                  </div>
                ) : (
                  <TextInput
                    key={inp.id}
                    value={inp.value as string}
                    onChangeFunc={e => handleChange(inp.id, e.currentTarget.value)}
                    placeholder={inp.placeholder}
                    errorMsg={inp.errorMessage}
                    isError={!inp.valid && errorInitiated}
                    label={inp.title}
                    isRequired={inp.required}
                    disabled={isReadOnly}
                    onBlur={e => handleChange(inp.id, e.currentTarget.value.trim())}
                  />
                )}
              </div>
            </>
          );
        case FormFieldType.SelectNew:
          return (
            <>
              {["patientRosterSize"].includes(inp.id) ? <div className="col-span-1" /> : null}
              <div className="col-span-2 md:col-span-1">
                <SelectNew
                  key={inp.id}
                  value={inp.value ? (inp.value as string) : undefined}
                  onSelect={e => handleChange(inp.id, e.currentTarget.value)}
                  placeHolder={inp.placeholder}
                  errorMsg={!inp.valid && errorInitiated ? inp.errorMessage : ""}
                  title={inp.title}
                  required={inp.required}
                  disabled={isReadOnly}
                  options={inp?.newOptions ?? []}
                  readOnly={isReadOnly}
                />
              </div>
            </>
          );
        case FormFieldType.Radio:
          return (
            <>
              {["physicianGroups", "acceptingPatients"].includes(inp.id) ? <div className="col-span-1" /> : null}
              {isReadOnly ? (
                <div className="col-span-2 md:col-span-1">
                  <label className="text-sm text-docsigna-blue-dark font-medium block w-full max-w-full mb-1">{inp.title}</label>
                  <h2 className="text-lg font-medium text-docsigna-purple">{inp.value}</h2>
                </div>
              ) : (
                <div className="col-span-2 md:col-span-1">
                  <RadioInput
                    key={inp.id}
                    value={inp.value as string}
                    onChangeFunc={e => handleChange(inp.id, e)}
                    errorMsg={!inp.valid && errorInitiated ? inp.errorMessage : ""}
                    label={inp.title}
                    isRequired={inp.required}
                    disabled={isReadOnly}
                    options={inp?.options ?? []}
                  />
                </div>
              )}
            </>
          );
        case FormFieldType.Places:
          return isReadOnly ? (
            <div className="col-span-2 md:col-span-1">
              <label className="text-sm text-docsigna-blue-dark font-medium block mb-1">{inp.title}</label>
              <div className="text-lg font-medium text-docsigna-purple">
                {currentOffice?.street && <div>{currentOffice.street}</div>}
                {currentOffice?.street1 && <div>{currentOffice.street1}</div>}
                <div>{[currentOffice?.city, currentOffice?.state, currentOffice?.zip].filter(Boolean).join(", ")}</div>
              </div>
            </div>
          ) : readOnly ? (
            <div
              className={`grid ${
                useCustomAddress || (currentOffice as any)?.customOfficeAddress === true ? "col-span-2" : "col-span-2 md:col-span-2"
              } grid-cols-1 gap-3 w-full`}
            >
              <AddressField
                label={inp.title}
                required={inp.required}
                initialValue={officeAddress?.value}
                initialComponents={officeAddress?.components}
                error={(!officeAddress?.isValid && errorInitiated) || addressValidate}
                errorMsg={inp?.errorMessage}
                initialData={currentOffice}
                disabled={!canEdit}
                onChange={val => {
                  setOfficeAddress(val);
                  setAddressValidate(!val?.isValid);
                }}
                onModeChange={isManual => {
                  setUseCustomAddress(isManual);
                }}
                hideGooglePlaceSearch={currentOffice?.customOfficeAddress ?? false}
                addressType={"office"}
                darkBg={false}
              />
            </div>
          ) : (
            <div className="col-span-2 md:col-span-2">
              <TextInput
                key={inp.id}
                value={inp.value as string}
                onChangeFunc={e => handleChange(inp.id, e.currentTarget.value)}
                placeholder={inp.placeholder}
                errorMsg={inp.errorMessage}
                isError={!inp.valid && errorInitiated}
                label={inp.title}
                isRequired={inp.required}
                disabled={isReadOnly}
                onBlur={e => handleChange(inp.id, e.currentTarget.value.trim())}
              />
            </div>
          );

        default:
          return null;
      }
    }
  };

  const populateFormData = (officeData: any) => {
    const data = [...formDataInitialState];
    const newData = data.map(item => {
      const newItem = { ...item };
      switch (item.id) {
        case "officeName":
          newItem.value = officeData.name;
          newItem.valid = true;
          break;
        case "officeAddress":
          const add = `${officeData?.street},${officeData?.street1}, ${officeData?.city}, ${officeData?.state}, ${officeData?.zip}, ${currentOffice?.country}`;
          newItem.value = add;
          newItem.valid = true;
          break;
        case "officePhone":
          newItem.value = formatPhoneNumber(officeData?.phone as string);
          newItem.valid = Phone_Fax_Regex.test(formatPhoneNumber(officeData?.phone as string));
          break;
        case "officeFax":
          newItem.value = formatPhoneNumber(officeData.fax as string);
          // newItem.valid = Phone_Fax_Regex.test(formatPhoneNumber(officeData.fax as string));
          newItem.valid = true;
          break;
        case "additionalPhone": {
          const raw = (officeData.additionalPhone as string) ?? "";
          const digitsOnly = raw.replace(/\D/g, "").slice(0, 6);
          newItem.value = digitsOnly;
          const len = digitsOnly.length;
          newItem.valid = len === 0 || (len >= 3 && len <= 6);
          break;
        }
        case "primaryContact":
          newItem.value = officeData.contactName;
          newItem.valid = true;
          break;
        case "primaryContactEmail":
          newItem.value = officeData.contactEmail;
          newItem.valid = validateEmail(officeData.contactEmail);
          break;
        case "familyPhysician":
        case "inPhysicianGroups":
        case "acceptingPatients":
          if (officeData[newItem.id as string] === true) {
            newItem.value = "Yes";
            newItem.valid = true;
          } else if (officeData[newItem.id as string] === false) {
            newItem.value = "No";
            newItem.valid = true;
          }
          break;
        case "specialty":
          if (typeof officeData["specialty"] !== "undefined" && officeData["specialty"] !== "") {
            newItem.value = officeData["specialty"];
            newItem.valid = true;
          }
          break;
        default:
          if (typeof officeData[newItem.id as string] !== "undefined") {
            newItem.value = officeData[newItem.id as string];
            newItem.valid = true;
          }
          break;
      }
      return newItem;
    });

    const familyPhysicianItem = newData.find(item => item.id === "familyPhysician");
    if (familyPhysicianItem?.value === "Yes") {
      newData.forEach(item => {
        if (item.id === "specialty") item.visible = false;
        if (["inPhysicianGroups", "patientRosterSize", "acceptingPatients"].includes(item.id)) item.visible = true;
      });
    } else if (familyPhysicianItem?.value === "No") {
      newData.forEach(item => {
        if (item.id === "specialty") item.visible = true;
        if (["inPhysicianGroups", "physicianGroups", "patientRosterSize", "acceptingPatients"].includes(item.id)) item.visible = false;
      });
    }

    const inPhysicianGroupsItem = newData.find(item => item.id === "inPhysicianGroups");
    if (inPhysicianGroupsItem?.value === "Yes") {
      newData.forEach(item => {
        if (item.id === "physicianGroups") item.visible = true;
      });
    }

    return newData;
  };

  const populateTimingData = (officeData: any) => {
    const data = [...officeWeekDayTimings];
    const newData = data.map(item => {
      const newItem = { ...item };
      if (typeof officeData[newItem.key3] !== "undefined" && officeData[newItem.key3]) {
        newItem.closed = true;
        newItem.valid = true;
      } else {
        if (typeof officeData[newItem.key1] !== "undefined" && !!officeData[newItem.key1]) {
          newItem.open = officeData[newItem.key1];
        }
        if (typeof officeData[newItem.key2] !== "undefined" && !!officeData[newItem.key2]) {
          newItem.close = officeData[newItem.key2];
        }
        newItem.valid = !!newItem.open && !!newItem.close;
      }
      return newItem;
    });
    return newData;
  };

  const setTemporarilyClosed = async (closed: boolean, reason?: string) => {
    setLoading(true);
    if (currentOffice?.officeId) {
      const res = await OfficeService.setOfficeTemporarilyClosed(currentOffice?.officeId, closed, reason);
      if (res) {
        await dispatch(getOffice({ officeId: currentOffice?.officeId }));
      }
    }
    setLoading(false);
  };

  const handleTemporarilyClosed = async () => {
    await setTemporarilyClosed(true, reasonText);
    setReasonText("");
    setShowTemporarilyCloseDialog(false);
  };

  const renderTemporarilyClose = () => {
    return !showPreview ? (
      <div className="">
        <h2 className="font-bold mb-2">
          Please confirm that you wish to close the office.
          <br />
          Provide patients additional details below.
        </h2>
        <TextInput
          maxLength={150}
          value={reasonText}
          placeholder="Enter reason"
          onChangeFunc={e => setReasonText(e.target.value)}
          isTextArea
          extraInputClass="resize-none"
        />
        <div className="flex justify-end mt-2">
          <span className={`text-sm text-docsigna-orange font-medium cursor-pointer`} onClick={() => setShowPreview(true)}>
            Preview
          </span>
        </div>
        <div className="flex justify-end gap-5 mt-3">
          <Button
            text="Confirm"
            variant={isLoading ? "loading" : "Primary"}
            disabled={!reasonText?.trim() || isLoading}
            onClick={handleTemporarilyClosed}
          />
          <Button text="Cancel" disabled={isLoading} variant="Outlined" onClick={() => setShowTemporarilyCloseDialog(false)} />
        </div>
      </div>
    ) : (
      <>
        <div className="flex flex-col gap-2">
          <h2 className="font-bold">{currentOffice?.name ?? ""} is currently closed.</h2>
          <p>{reasonText}</p>
          <p>Your request will not be reviewed until the office has reopened.</p>
        </div>
        <div className="flex justify-end mt-3">
          <span className={`text-sm text-docsigna-orange font-medium cursor-pointer`} onClick={() => setShowPreview(false)}>
            Close Preview
          </span>
        </div>
      </>
    );
  };
  const { pssData } = useSelector((state: RootState) => state.currentUser);
  useEffect(() => {
    if (currentUser?.userType === "system-admin") {
      if (currentOffice && currentOffice?.officeId) {
        dispatch(getPssData({ officeId: currentOffice?.officeId }));
      }
    }
  }, [currentOffice]);

  const deleteFile = () => {
    if (currentOffice?.officeId) {
      const res = dispatch(
        deleteOfficeFile({
          officeId: currentOffice?.officeId,
          type: "letter-header-logo",
        })
      ).then(action => {
        if (action?.payload && currentOffice.officeId) {
          dispatch(getOffice({ officeId: currentOffice.officeId }));
          toast?.openToast("File deleted successfully.", 2000, ToastVariants.Success);
        } else toast?.openToast("Something went wrong.", 2000, ToastVariants.Error);
      });
    }
  };

  return (
    <div className="relative md:flex gap-5">
      <div className="w-full">
        <div className="flex flex-col justify-between gap-5">
          <div id="office-info-section-1" className={`grid ${canEdit ? "grid-cols-1" : "grid-cols-1 md:grid-cols-2"} gap-5 w-full`}>
            {formData.slice(0, 7).map((input, index) => {
              return renderInput(input, !canEdit, index);
            })}
          </div>
          {pssData?.officePss && (
            <>
              {/* Letterhead Configuration Section */}
              <div id="office-info-letterhead" className="w-full">
                <div className="mb-6">
                  <div className="flex items-center justify-between pb-1 border-b border-docsigna-purple border-opacity-40 py-1 my-2 col-span-2">
                    <h2 className="uppercase text-lg font-bold">Letterhead Configuration</h2>
                    <Button
                      onClick={handleLetterheadPreview}
                      text="Preview"
                      variant="Primary"
                      // disabled={!letterheadContent.trim() || isSystemAdmin}
                      disabled={!letterheadContent?.trim()}
                      width="fit"
                    />
                  </div>
                  <p className="text-sm text-gray-600 mb-4">
                    Enter your letterhead content below. This will be used for document generation and preview.
                  </p>
                </div>

                {!canEdit ? (
                  <div className="">
                    <h2 className="uppercase text-lg font-bold">Letterhead CONTENT</h2>

                    <div
                      className="border bg-[#f1e9e93b] border-gray-400 p-3"
                      dangerouslySetInnerHTML={{
                        __html: letterheadContent ? letterheadContent?.replace(/\n/g, "<br/>") : "Letterhead Not Found",
                      }}
                    />
                  </div>
                ) : (
                  <div className="mb-6">
                    <TextArea
                      label="Letterhead Content"
                      value={letterheadContent}
                      onChange={handleLetterheadChange}
                      placeholder="Enter your letterhead content here...&#10;Example:&#10;Name&#10;City&#10;State&#10;Street&#10;"
                      isRequired={true}
                      extraTextAreaClass=" min-h-32"
                    />
                  </div>
                )}
              </div>
              <div>
                <div className="flex border-b border-docsigna-purple border-opacity-40 py-1 mb-2 col-span-2">
                  <h2 className="uppercase text-lg font-bold">Letterhead Logo</h2>
                </div>
                {!canEdit ? (
                  <div className="mb-4">
                    {letterheadLogoFileData ? (
                      <div className="mt-3 flex justify-between">
                        <span className="text-sm text-gray-600 mb-2">
                          <b>FileName: </b>
                          {letterheadLogoFileData?.originalFilename}
                        </span>
                        <span>
                          <DeleteOutline htmlColor={colors.Orange} className="cursor-pointer" onClick={() => deleteFile()} />
                        </span>
                      </div>
                    ) : (
                      <div className="text-sm text-gray-500">No letter logo uploaded</div>
                    )}
                  </div>
                ) : (
                  <div className="mb-6">
                    <label>Please upload a logo with dimensions 125 × 125 pixels</label>
                    <DropArea
                      allowedFileTypes={[FileTypes.PNG, FileTypes.JPEG, FileTypes.JPG]}
                      maxFileSize={2 * 1024 * 1024} // 2MB
                      maxNumOfFiles={1}
                      onFilesChange={fileIds => {
                        // fileIds is an array of uploaded file IDs, but you can also use handleFileChange for local file
                        if (!fileIds.length) {
                          // Clear file state when file is deleted
                          setLetterHeaderLogoFileId(undefined);
                          setLetterheadLogoFile(null);
                          if (letterheadLogoPreviewUrl) {
                            URL.revokeObjectURL(letterheadLogoPreviewUrl);
                            setLetterheadLogoPreviewUrl(null);
                          }
                          return;
                        }
                        const latestId = fileIds[fileIds.length - 1];
                        setLetterHeaderLogoFileId(latestId);
                      }}
                      handleFileChange={file => {
                        // Clear previous preview URL when new file is selected
                        if (letterheadLogoPreviewUrl) {
                          URL.revokeObjectURL(letterheadLogoPreviewUrl);
                          setLetterheadLogoPreviewUrl(null);
                        }
                        setLetterheadLogoFile(file);
                        setLetterheadLogoDropAreaError("");
                      }}
                      onError={err => {
                        setLetterheadLogoDropAreaError(onFileError(err, "2MB"));
                      }}
                      dropAreaText="PNG, JPG, or JPEG logo (max 2MB)"
                      isLocalUpload
                      isImageType={true}
                    />
                    {letterheadLogoDropAreaError && <p className="text-red-500 text-sm mt-1">{letterheadLogoDropAreaError}</p>}
                    <div className="text-sm mt-3">
                      {letterheadLogoFileData ? (
                        <div>
                          <b>Current File: </b>
                          {letterheadLogoFileData?.originalFilename}
                        </div>
                      ) : letterheadLogoFile ? (
                        <div>
                          <b>New File: </b>
                          {letterheadLogoFile.name}
                        </div>
                      ) : (
                        <div className="text-gray-500">No letter logo uploaded</div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </>
          )}

          <div id="office-info-section-2" className="grid grid-cols-1 md:grid-cols-2 gap-5 w-full">
            {formData.slice(7, 10).map((input, index) => {
              return renderInput(input, !canEdit, index);
            })}
          </div>
          <div id="office-info-section-3" className="grid grid-cols-1 md:grid-cols-2 gap-5 w-full">
            {formData.slice(10).map((input, index) => {
              return renderInput(input, !canEdit, index);
            })}
          </div>
          <div id="office-info-section-4" className="grid grid-cols-1 md:grid-cols-2 gap-5 w-full">
            <div className="flex gap-5 col-span-2">
              <div
                className={`flex border-b border-docsigna-purple border-opacity-40 py-1 ${
                  currentOffice?.officeState === OfficeState.Published ? "w-2/3" : "w-full"
                }`}
              >
                <h2 className="uppercase text-lg font-bold">OFFICE HOURS</h2>
                {/* {canEdit ? <span className="text-red-500">*</span> : null}
                {errorInitiated && !currentOffice?.tempClosed && !validTimings ? (
                  <p className="ml-5" style={{ color: colors.Red }}>
                    Please provide office hours
                  </p>
                ) : null} */}
              </div>
              {currentOffice?.officeState === OfficeState.Published ? (
                <div className="flex justify-end">
                  {!currentOffice?.tempClosed ? (
                    <Button width="fit" onClick={() => setShowTemporarilyCloseDialog(true)} text="Temporarily Close Office" />
                  ) : (
                    <Button width="fit" variant={isLoading ? "loading" : "Primary"} onClick={() => setTemporarilyClosed(false)} text="Open Office" />
                  )}
                </div>
              ) : null}
            </div>
            <div className="relative col-span-2">
              {currentOffice?.tempClosed ? (
                <div className="absolute w-full h-full bg-white/85 backdrop-blur-[2px] text-center flex justify-start items-center">
                  <h2 className="font-bold text-4xl text-docsigna-orange uppercase">
                    Office
                    <br />
                    TEMPORARILY
                    <br />
                    Closed
                  </h2>
                </div>
              ) : null}
              {weekDayTimings.map((item, index) => {
                return (
                  <div key={`${item.id}-${index}-${item.title}`} className="col-span-2 md:flex w-full items-center mb-3">
                    <h2 className="w-4/12">{item.title}</h2>
                    {!canEdit ? (
                      <div className="flex items-center">
                        <h2 className="w-24 text-lg font-medium text-docsigna-purple">
                          {item.closed ? "Closed" : item.open ? moment(item.open, "HH:mm:ss").format("hh:mm A") : ""}
                        </h2>
                        <span className="ml-6 mr-10">-</span>
                        <h2 className="w-24 text-lg font-medium text-docsigna-purple">
                          {item.closed ? "Closed" : item.close ? moment(item.close, "HH:mm:ss").format("hh:mm A") : ""}
                        </h2>
                      </div>
                    ) : (
                      <div className="flex items-center gap-2 md:gap-10">
                        <SelectNew
                          title=""
                          required
                          onSelect={e => handleChangeTime(item, index, "open", e.currentTarget.value)}
                          value={item.open && !item.closed ? item.open : undefined}
                          disabled={item.closed}
                          placeHolder={item.closed ? "-" : "Open"}
                          options={hoursList}
                        />
                        <span>-</span>
                        <SelectNew
                          title=""
                          required
                          onSelect={e => handleChangeTime(item, index, "close", e.currentTarget.value)}
                          value={item.close && !item.closed ? item.close : undefined}
                          disabled={item.closed}
                          placeHolder={item.closed ? "-" : "Close"}
                          options={hoursList}
                        />
                        <Checkbox
                          label="Closed"
                          checked={item.closed}
                          onChange={val => handleChangeTime(item, index, "closed", val.target.checked)}
                        />
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>
      <div className="py-2">
        {isAuthorizedToEdit ? (
          canEdit ? (
            <Button
              className="profile-btn"
              width="fit"
              onClick={() => {
                setErrorInitiated(true);
                if (validateForm()) saveOfficeProfile();
              }}
              disabled={(!validateForm() && errorInitiated) || loading}
              text="Save"
            />
          ) : (
            <Button
              className="profile-btn"
              width="fit"
              onClick={() => {
                setCanEdit(true);
              }}
              text="Edit"
            />
          )
        ) : null}
      </div>
      <ConfirmationDialog
        open={showTemporarilyCloseDialog}
        title=""
        description={renderTemporarilyClose()}
        handleFailure={() => undefined}
        handleSuccess={() => undefined}
        successButtonText=""
        failureButtonText=""
        hideActionButtons={false}
      />

      {/* Letterhead Preview Modal */}
      <Modal open={showLetterheadPreview} onClose={closeLetterheadPreview}>
        <div className="modal relative z-10">
          <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity">
            <div className="fixed inset-0 z-10 overflow-y-auto">
              <div className="flex min-h-full items-center justify-center p-4 text-center sm:items-center sm:p-0">
                <div className="relative transform overflow-hidden rounded-lg bg-white text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-4xl">
                  <div className="bg-white p-4 xl:p-6">
                    <span className="modal-close absolute top-2 right-2 w-7 h-7 bg-white rounded-full">
                      <img
                        src={close}
                        alt="Close letterhead preview"
                        role="button"
                        className="w-full h-full cursor-pointer"
                        onClick={closeLetterheadPreview}
                      />
                    </span>

                    <div className="mb-4">
                      <h3 className="text-lg font-semibold text-docsigna-blue-dark">Letterhead Preview</h3>
                    </div>

                    {/* PDF-like Preview Container */}
                    <div
                      className="w-full bg-white border border-gray-300 shadow-lg"
                      style={{
                        minHeight: "500px",
                        maxHeight: "70vh",
                        overflow: "auto",
                      }}
                    >
                      {/* Letterhead Content */}
                      <div className="p-8">
                        <div className="relative">
                          {(letterheadLogoPreviewUrl || letterheadLogoFileData?.url) && (
                            <div className="absolute">
                              <img
                                className="max-h-[125px] max-w-[125px]"
                                src={letterheadLogoPreviewUrl || letterheadLogoFileData?.url}
                                alt="Letterhead Logo"
                              />
                            </div>
                          )}
                          <div className="flex justify=center">
                            <div className="w-[100%]">
                              <div className="mb-8 flex flex-col items-center h-[130px]">
                                {/* <p className="font-medium text-gray-700 capitalize">{currentOffice?.name}</p> */}
                                {/* {currentOffice?.letterHeader?.isLetterHeaderAddress && (
                                  <p className="font-medium text-dcsigna-blue-dark">
                                    {currentOffice?.street +
                                      ", " +
                                      currentOffice?.city +
                                      ", " +
                                      currentOffice?.state +
                                      ", " +
                                      currentOffice?.billingCountry || "Office address"}
                                  </p>
                                )} */}
                                {letterheadContent?.split("\n").map((line, index) => (
                                  <div key={index} className="text-sm text-gray-800 mb-1">
                                    {line || "\u00A0"}
                                  </div>
                                ))}
                              </div>
                            </div>
                          </div>
                        </div>

                        {/* Sample Document Content */}
                        <div className="border-t border-gray-300 pt-6">
                          <div className="text-sm text-gray-600 mb-4">
                            <strong>Date:</strong> {new Date().toLocaleDateString()}
                          </div>
                          <div className="text-sm text-gray-600 mb-4">
                            <strong>To:</strong> [Recipient Name]
                            <br />
                            [Recipient Address]
                            <br />
                            [City, State ZIP]
                          </div>
                          <div className="text-sm text-gray-600 mb-4">
                            <strong>Subject:</strong> [Document Subject]
                          </div>
                          <div className="text-sm text-gray-600 leading-relaxed">
                            <p className="mb-4">Dear [Recipient Name],</p>
                            <p className="mb-4">
                              This is a sample document to demonstrate how your letterhead will appear on official documents. The content above
                              represents your letterhead information that will be displayed at the top of all generated documents.
                            </p>
                            <p className="mb-4">You can customize this letterhead content anytime by editing the text in the input field above.</p>
                            <p className="mb-4">
                              Sincerely,
                              <br />
                              [Your Name]
                              <br />
                              [Your Title]
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default OfficeProfileTab;
